import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import FeatureGate from '../components/FeatureGate';
import usePullToRefresh from '../hooks/usePullToRefresh';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import { PRODUCT_TYPE } from '../config/product';

function ExpensesContent() {
  const navigate = useNavigate();
  const { logout, isAdmin, isDriver, isSuperAdmin } = useAuth();
  const { getAppName } = useConfig();

  const [expenses, setExpenses] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({ byCategory: [], grandTotal: 0, totalCount: 0, periodLabel: '' });
  const [summaryPeriod, setSummaryPeriod] = useState('this_month');
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Filters for expense list
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRoute, setFilterRoute] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Helper to get category label
  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.name.toLowerCase() === value?.toLowerCase() || c.name === value);
    return cat ? cat.name : value;
  };

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: 'Fuel',
    description: '',
    routeId: '',
    helperName: '',
  });

  const loadCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/expense-categories`);
      if (response.data?.categories) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Failed to load expense categories:', err);
    }
  }, []);

  const loadSummary = useCallback(async (period = summaryPeriod) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/expenses/summary?period=${period}&product=${PRODUCT_TYPE}`);
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to load expense summary:', err);
    }
  }, [summaryPeriod]);

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('product', PRODUCT_TYPE);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      if (filterCategory) params.append('category', filterCategory);
      if (filterRoute) params.append('routeId', filterRoute);

      const [expensesRes, routesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/expenses?${params}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/expenses/routes?product=${PRODUCT_TYPE}`),
      ]);

      setExpenses(expensesRes.data.expenses || expensesRes.data);
      setRoutes(routesRes.data);
    } catch (err) {
      console.error('Failed to load expenses:', err);
      if (err.response?.status === 403) {
        console.error('Tier restriction:', err.response.data);
      }
    } finally {
      setLoading(false);
    }
  }, [filterStartDate, filterEndDate, filterCategory, filterRoute]);

  const handleSummaryPeriodChange = (e) => {
    const newPeriod = e.target.value;
    setSummaryPeriod(newPeriod);
    loadSummary(newPeriod);
  };

  const { PullToRefreshIndicator } = usePullToRefresh(loadData);

  useEffect(() => {
    loadCategories();
    loadSummary();
    loadData();
  }, [loadCategories, loadSummary, loadData]);

  // Pagination for expenses list
  const {
    paginatedData: paginatedExpenses,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalItems
  } = usePagination(expenses, 'expenses', 25);

  const openAddModal = () => {
    setEditingExpense(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      category: categories.length > 0 ? categories[0].name : 'Fuel',
      description: '',
      routeId: '',
      helperName: '',
    });
    setError('');
    setSuccessMessage('');
    setShowModal(true);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setFormData({
      date: expense.date.split('T')[0],
      amount: expense.amount,
      category: expense.category,
      description: expense.description || '',
      routeId: expense.route_id || '',
      helperName: expense.helper_name || '',
    });
    setError('');
    setSuccessMessage('');
    setShowModal(true);
  };

  const handleSubmit = async (e, addAnother = false, switchToAddMode = false) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const payload = {
        date: formData.date,
        amount: parseFloat(formData.amount),
        category: formData.category,
        description: formData.description || null,
        routeId: formData.routeId || null,
        helperName: formData.category.toLowerCase() === 'labor' ? formData.helperName : null,
        productType: PRODUCT_TYPE,
      };

      if (editingExpense) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/expenses/${editingExpense.id}`, payload);

        if (switchToAddMode) {
          // Switch to add mode, keep date and routeId, clear other fields
          setEditingExpense(null);
          setFormData({
            date: formData.date,
            amount: '',
            category: categories.length > 0 ? categories[0].name : 'Fuel',
            description: '',
            routeId: formData.routeId,
            helperName: '',
          });
          setSuccessMessage('Expense updated! Now adding new expense.');
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setShowModal(false);
        }
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/expenses`, payload);

        if (addAnother) {
          // Keep modal open, keep date and routeId, clear other fields
          setFormData({
            ...formData,
            amount: '',
            category: categories.length > 0 ? categories[0].name : 'Fuel',
            description: '',
            helperName: '',
          });
          setSuccessMessage('Expense added!');
          // Clear success message after 3 seconds
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setShowModal(false);
        }
      }

      loadData();
    } catch (err) {
      console.error('Save expense error:', err);
      console.error('Response:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/expenses/${id}`);
      loadData();
    } catch (err) {
      alert('Failed to delete expense');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Note: expense date is pre-formatted by backend as "Mon DD, YYYY"
  // so we display it directly without Date parsing

  return (
    <div className="expenses-page">
      {/* Header */}
      <header className="app-header">
        <div className="app-header-content">
          <span className="app-logo">{getAppName()}</span>
          <nav className="header-nav desktop-nav">
            <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => navigate('/customers')} className="nav-link">Customers</button>}
            <button onClick={() => navigate('/routes')} className="nav-link">Routes</button>
            {isAdmin() && <button onClick={() => navigate('/history')} className="nav-link">History</button>}
            {isAdmin() && <span className="nav-link active">Expenses</span>}
            {isAdmin() && <button onClick={() => navigate('/reports')} className="nav-link">Reports</button>}
            <button onClick={() => navigate('/service-mode')} className="nav-link storm-link">Service Mode</button>
            <button onClick={() => navigate('/settings')} className="nav-link">Settings</button>
            <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>
            {isSuperAdmin && isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="mobile-nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => { navigate('/customers'); setMobileMenuOpen(false); }} className="mobile-nav-link">Customers</button>}
            <button onClick={() => { navigate('/routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">Routes</button>
            {isAdmin() && <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="mobile-nav-link">History</button>}
            {isAdmin() && <span className="mobile-nav-link active">Expenses</span>}
            {isAdmin() && <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="mobile-nav-link">Reports</button>}
            <button onClick={() => { navigate('/service-mode'); setMobileMenuOpen(false); }} className="mobile-nav-link storm-link">Service Mode</button>
            <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-link">Settings</button>
            <button onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }} className="mobile-nav-link">Help</button>
            {isSuperAdmin && isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <PullToRefreshIndicator />

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header flex-between">
          <div>
            <h1 className="page-title">Expenses</h1>
            <p className="text-muted">{summary.totalCount} expenses tracked</p>
          </div>
          <button className="btn btn-primary" onClick={openAddModal}>
            + Add Expense
          </button>
        </div>

        {/* Expense Summary Widget */}
        <div className="expense-summary-widget">
          <div className="expense-summary-header">
            <h2 className="expense-summary-title">Expense Summary</h2>
            <select
              className="expense-period-select"
              value={summaryPeriod}
              onChange={handleSummaryPeriodChange}
            >
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_season">This Season</option>
              <option value="ytd">Year to Date</option>
              <option value="all_time">All Time</option>
            </select>
          </div>
          <div className="expense-summary-content">
            {summary.byCategory.length === 0 ? (
              <div className="expense-summary-empty">No expenses for {summary.periodLabel || 'this period'}</div>
            ) : (
              <>
                {summary.byCategory.map((cat) => {
                  const percentage = summary.grandTotal > 0 ? ((parseFloat(cat.total) / summary.grandTotal) * 100).toFixed(0) : 0;
                  return (
                    <div key={cat.category} className="expense-summary-row">
                      <div className="expense-summary-info">
                        <span className="expense-category-name">{getCategoryLabel(cat.category)}</span>
                        <span className="expense-category-amount">{formatCurrency(cat.total)}</span>
                      </div>
                      <div className="expense-bar-container">
                        <div
                          className="expense-bar"
                          style={{ width: `${percentage}%` }}
                        />
                        <span className="expense-bar-label">{percentage}%</span>
                      </div>
                    </div>
                  );
                })}
                <div className="expense-summary-total">
                  <span>Total Expenses:</span>
                  <span className="expense-total-amount">{formatCurrency(summary.grandTotal)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <select
            className="form-input filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
          <select
            className="form-input filter-select"
            value={filterRoute}
            onChange={(e) => setFilterRoute(e.target.value)}
          >
            <option value="">All Routes</option>
            {routes.map((route) => (
              <option key={route.id} value={route.id}>{route.label || route.name}</option>
            ))}
          </select>
          <input
            type="date"
            className="form-input filter-date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            placeholder="Start Date"
          />
          <input
            type="date"
            className="form-input filter-date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            placeholder="End Date"
          />
          {(filterCategory || filterRoute || filterStartDate || filterEndDate) && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                setFilterCategory('');
                setFilterRoute('');
                setFilterStartDate('');
                setFilterEndDate('');
              }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Expenses List */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : expenses.length === 0 ? (
          <div className="empty-state">
            <p>No expenses found</p>
            <button className="btn btn-primary" onClick={openAddModal}>Add Your First Expense</button>
          </div>
        ) : (
          <>
            <div className="expenses-list">
              {paginatedExpenses.map((expense) => (
                <div key={expense.id} className="expense-card">
                  <div className="expense-main">
                    <div className="expense-amount">{formatCurrency(expense.amount)}</div>
                    <div className="expense-details">
                      <span className={`category-badge ${expense.category}`}>
                        {getCategoryLabel(expense.category)}
                      </span>
                      <span className="expense-date">{expense.date}</span>
                    </div>
                  </div>
                  <div className="expense-info">
                    {expense.description && <p className="expense-description">{expense.description}</p>}
                    {expense.helper_name && (
                      <p className="expense-helper">Helper: {expense.helper_name}</p>
                    )}
                    {expense.route_name && (
                      <p className="expense-route">Route: {expense.route_name}</p>
                    )}
                  </div>
                  <div className="expense-actions">
                    <button className="btn btn-sm btn-outline" onClick={() => openEditModal(expense)}>Edit</button>
                    <button className="btn btn-sm btn-danger-outline" onClick={() => handleDelete(expense.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingExpense ? 'Edit Expense' : 'Add Expenses'}</h2>

            {successMessage && <div className="alert alert-success">{successMessage}</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="form-input"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Category *</label>
                <select
                  className="form-input"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {formData.category.toLowerCase() === 'labor' && (
                <div className="form-group">
                  <label className="form-label">Worker Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.helperName}
                    onChange={(e) => setFormData({ ...formData, helperName: e.target.value })}
                    placeholder="Enter worker's name"
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Link to Route (Optional)</label>
                <select
                  className="form-input"
                  value={formData.routeId}
                  onChange={(e) => setFormData({ ...formData, routeId: e.target.value })}
                >
                  <option value="">-- No Route --</option>
                  {routes.map((route) => (
                    <option key={route.id} value={route.id}>{route.label || route.name}</option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                {editingExpense ? (
                  <>
                    <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={saving}
                      onClick={(e) => handleSubmit(e, false, true)}
                    >
                      {saving ? 'Saving...' : 'Update & Add New'}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Saving...' : 'Update & Close'}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={saving}
                      onClick={(e) => handleSubmit(e, true)}
                    >
                      {saving ? 'Saving...' : 'Add & Continue'}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Saving...' : 'Add & Close'}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .summary-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .summary-card.total {
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: white;
        }

        .summary-label {
          font-size: 0.85rem;
          opacity: 0.8;
          margin-bottom: 4px;
        }

        .summary-value {
          font-size: 1.5rem;
          font-weight: 700;
        }

        .summary-count {
          font-size: 0.75rem;
          opacity: 0.7;
          margin-top: 4px;
        }

        /* Expense Summary Widget */
        .expense-summary-widget {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          margin-bottom: 24px;
        }

        .expense-summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .expense-summary-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0;
          color: var(--color-text);
        }

        .expense-period-select {
          padding: 6px 10px;
          font-size: 0.85rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: var(--color-background);
          color: var(--color-text);
          cursor: pointer;
        }

        .expense-summary-content {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .expense-summary-empty {
          text-align: center;
          padding: 24px;
          color: var(--color-text-light);
          font-size: 0.9rem;
        }

        .expense-summary-row {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .expense-summary-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .expense-category-name {
          font-weight: 500;
          color: var(--color-text);
          font-size: 0.9rem;
        }

        .expense-category-amount {
          font-weight: 600;
          color: var(--color-text);
          font-size: 0.9rem;
        }

        .expense-bar-container {
          height: 20px;
          background: var(--color-background);
          border-radius: 10px;
          overflow: hidden;
          position: relative;
        }

        .expense-bar {
          height: 100%;
          background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          border-radius: 10px;
          min-width: 2px;
          transition: width 0.3s ease;
        }

        .expense-bar-label {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--color-text-light);
        }

        .expense-summary-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 12px;
          border-top: 1px solid var(--color-border);
          margin-top: 8px;
          font-size: 1rem;
          font-weight: 600;
        }

        .expense-total-amount {
          color: var(--color-primary);
          font-size: 1.1rem;
        }

        .filters-bar {
          display: flex;
          gap: 12px;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .filter-select, .filter-date {
          max-width: 180px;
        }

        .expenses-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .expense-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          align-items: flex-start;
        }

        .expense-main {
          flex: 0 0 auto;
          min-width: 120px;
        }

        .expense-amount {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--color-text);
        }

        .expense-details {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-top: 4px;
          flex-wrap: wrap;
        }

        .category-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .category-badge.Labor { background: #dbeafe; color: #1e40af; }
        .category-badge.Materials { background: #fef3c7; color: #92400e; }
        .category-badge.Fuel { background: #fee2e2; color: #991b1b; }
        .category-badge.Equipment { background: #e0e7ff; color: #3730a3; }
        .category-badge.Repairs { background: #fce7f3; color: #9d174d; }
        .category-badge.Salt { background: #cffafe; color: #0e7490; }
        .category-badge.Other { background: #e5e7eb; color: #374151; }

        .expense-date {
          font-size: 0.85rem;
          color: var(--color-text-light);
        }

        .expense-info {
          flex: 1;
          min-width: 200px;
        }

        .expense-description {
          font-size: 0.9rem;
          color: var(--color-text);
          margin: 0 0 4px 0;
        }

        .expense-helper, .expense-route {
          font-size: 0.8rem;
          color: var(--color-text-light);
          margin: 0;
        }

        .expense-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .btn-danger-outline {
          border: 1px solid #ef4444;
          color: #ef4444;
          background: transparent;
        }

        .btn-danger-outline:hover {
          background: #fef2f2;
        }

        .empty-state {
          text-align: center;
          padding: 48px 20px;
          color: var(--color-text-light);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        /* Mobile card layout */
        @media (max-width: 768px) {
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-select, .filter-date {
            max-width: 100%;
          }

          .summary-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .summary-card {
            padding: 12px;
          }

          .summary-value {
            font-size: 1.25rem;
          }

          .expense-card {
            flex-direction: column;
            padding: 16px;
            gap: 12px;
          }

          .expense-main {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            width: 100%;
            min-width: unset;
          }

          .expense-amount {
            font-size: 1.5rem;
            font-weight: 700;
          }

          .expense-details {
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;
            margin-top: 0;
          }

          .expense-date {
            font-size: 0.8rem;
          }

          .expense-info {
            width: 100%;
            min-width: unset;
            padding-top: 8px;
            border-top: 1px solid #f0f0f0;
          }

          .expense-description {
            font-size: 0.9rem;
            margin-bottom: 8px;
          }

          .expense-helper, .expense-route {
            font-size: 0.85rem;
            margin-bottom: 4px;
          }

          .expense-actions {
            width: 100%;
            justify-content: stretch;
            gap: 12px;
            padding-top: 12px;
            border-top: 1px solid #f0f0f0;
          }

          .expense-actions .btn {
            flex: 1;
            justify-content: center;
            padding: 10px 16px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .page-header.flex-between {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .page-header .btn {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .summary-grid {
            grid-template-columns: 1fr 1fr;
          }

          .expense-amount {
            font-size: 1.35rem;
          }
        }

        /* Nav styles */
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
        }
      `}</style>
    </div>
  );
}

// Wrap the content in FeatureGate
function Expenses() {
  return (
    <FeatureGate feature="expense_tracking">
      <ExpensesContent />
    </FeatureGate>
  );
}

export default Expenses;
